import React, { useState, useEffect, useMemo, useContext } from "react";
import { Amplify } from "aws-amplify";
import { signOut } from "aws-amplify/auth";
import { Authenticator, defaultTheme, ThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import awsExports from "./aws-exports";
// import { Amplify, Auth, Hub } from 'aws-amplify';

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import routesData from "routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { fetchAuthSession } from "aws-amplify/auth";

import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import blueParkingIcon from "assets/images/logo_blueParking.png";
import { Navigate, Route, Routes } from "react-router-dom";
import Management from "./layouts/management";
import Icon from "@mui/material/Icon";
import { jwtDecode } from "jwt-decode";
import logoWhite from "assets/images/logos/logo-WHITE.png";
import logoMixed from "assets/images/logos/logo-mixed.png";
import partLogo from "assets/images/logos/partLogo.png";
import partLogoDark from "assets/images/logos/partLogoDark.png";
import PlaceDetails from "./layouts/placeDetails";
import OrganizationDetails from "./layouts/organizationDetails";
import DeviceDetails from "./layouts/deviceDetails/deviceDetails";
import { PopupContext } from "./context/popupContext";
import io from "socket.io-client";
import GodsBlueEye from "./layouts/godsblueeye";
import MasterOperation from "./layouts/masteroperation";
import { useIdleTimer } from "react-idle-timer";
import MasterSales from "./layouts/mastersales";
import MasterFinance from "./layouts/masterfinance";
import IntercomReports from "./layouts/intercomReports";
import Tarrifs from "layouts/tariffs";
Amplify.configure(awsExports);

function RequireAdmin({ children }) {
  const [allowed, setAllowed] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.accessToken?.toString();
        if (!token) return setAllowed(false);
        const decoded = jwtDecode(token);
        const groups = decoded["cognito:groups"] || [];
        setAllowed(groups.includes("admin"));
      } catch {
        setAllowed(false);
      }
    })();
  }, []);

  if (allowed === null) return null; // or a spinner/splash
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;
  const socket = useMemo(
    () =>
      io(
        "https://blue-parking-invite.bluept.dev",
        { transports: ["websocket"] } // skip long-polling => no CORS preflight
      ),
    []
  );

  const [rtlCache, setRtlCache] = useState(() => createCache({ key: "default" }));
  const [routes, setRoutes] = useState(routesData);
  const [user, setUser] = useState(null);
  const { showPopup } = useContext(PopupContext);
  useIdleTimer({
    timeout: 1000 * 60 * 60 * 18,
    onIdle: async () => {
      await signOut(); // revoke tokens client-side
      window.location.reload(); // take user to sign-in screen
    },
    debounce: 500,
  });
  // Dynamically update routes based on authentication
  const updateRoutes = async () => {
    const isAdmin = async () => {
      const session = await fetchAuthSession();
      const token = session.tokens.accessToken.toString();
      if (!token) return false;
      try {
        const decoded = jwtDecode(token);
        const groups = decoded["cognito:groups"] || [];
        return groups.includes("admin");
      } catch (e) {
        return false;
      }
    };

    const newRoutes = [...routesData];
    if (await isAdmin()) {
      newRoutes.splice(1, 0, {
        type: "collapse",
        name: "Master Marketing",
        key: "mastersales",
        icon: <Icon fontSize="small">devices</Icon>,
        route: "/mastersales",
        component: <MasterSales />,
      });
      newRoutes.splice(2, 0, {
        type: "collapse",
        name: "Master Finance",
        key: "masterfinance",
        icon: <Icon fontSize="small">money</Icon>,
        route: "/masterfinance",
        component: <MasterFinance />,
      });
      newRoutes.splice(3, 0, {
        type: "collapse",
        name: "Master Operation",
        key: "masteroperation",
        icon: <Icon fontSize="small">money</Icon>,
        route: "/masteroperation",
        component: <MasterOperation />,
      });
      newRoutes.splice(4, 0, {
        type: "collapse",
        name: "Gods Blue Eye",
        key: "godsblueeye",
        icon: <Icon fontSize="small">visibility</Icon>,
        route: "/godsblueeye",
        component: <GodsBlueEye />,
      });
      newRoutes.splice(5, 0, {
        type: "collapse",
        name: "Intercom Reports",
        key: "intercomreports",
        icon: <Icon fontSize="small">report</Icon>,
        route: "/intercom-reports",
        component: <IntercomReports />,
      });
      newRoutes.push({
        type: "collapse",
        name: "Management",
        key: "management",
        icon: <Icon fontSize="small">assignment</Icon>,
        route: "/management",
        component: <Management />,
      });
      newRoutes.push({
        type: "collapse",
        name: "Tarrifs",
        key: "Tarrifs",
        icon: <Icon fontSize="small">attach_money</Icon>,
        route: "/tarrifs",
        component: <Tarrifs />,
      });
    }
    setRoutes(newRoutes);
  };

  // Update Emotion Cache on direction change
  useMemo(() => {
    const newCache = createCache({
      key: direction === "rtl" ? "rtl" : "ltr",
      stylisPlugins: direction === "rtl" ? [rtlPlugin] : [],
    });
    setRtlCache(newCache);
  }, [direction]);

  useEffect(() => {
    (async function f() {
      if (user) {
        await updateRoutes();
      } else {
        setRoutes(routesData);
      }
    })();
  }, [user]);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  const handleOnMouseEnter = () => {
    if (miniSidenav) {
      setMiniSidenav(dispatch, false);
    }
  };

  const handleOnMouseLeave = () => {
    setMiniSidenav(dispatch, true);
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);



  // Updated useEffect to trigger popups every 30 seconds and via socket events
  // useEffect(() => {
  //   socket.on("showPopup", (data) => {
  //     showPopup(data.message);
  //   });
  //
  //   const intervalId = setInterval(() => {
  //     const simulatedMessage = `Simulated popup at ${new Date().toLocaleTimeString()}`;
  //     showPopup(simulatedMessage);
  //   }, 1000);
  //
  //   return () => {
  //     socket.off("showPopup");
  //     clearInterval(intervalId);
  //   };
  // }, []);

  useEffect(() => {
    socket.on("godsBlueEye:newRows", (newRows) => {
      // showPopup can be whatever UI you already have wired up:
      newRows.forEach((row) => {
        showPopup(`New “Gods Blue Eye” event: Car ${row.car_id} at ${row.place}`);
      });
    });

    return () => {
      socket.off("godsBlueEye:newRows");
    };
  }, [socket, showPopup]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });

  const customTheme = {
    ...defaultTheme,
    name: "custom-theme",
    tokens: {
      colors: {
        brand: {
          primary: { value: "#007FFF" },
          secondary: { value: "#007FFF" },
        },
      },
      components: {
        button: {
          primary: {
            backgroundColor: { value: "#007FFF" },
            color: { value: "#FFFFFF" },
          },
        },
      },
    },
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Authenticator
        components={{
          SignIn: {
            Header: () => (
              <div style={{ textAlign: "center" }}>
                <img src={blueParkingIcon} alt="Blue Parking" />
              </div>
            ),
          },
        }}
        hideSignUp={true}
      >
        {({ user: authUser, signOut }) => {
          if (authUser && user !== authUser) {
            setUser(authUser);
          }
          return (
            <CacheProvider value={rtlCache}>
              <MuiThemeProvider theme={darkMode ? themeDark : theme}>
                <CssBaseline />
                {layout === "dashboard" && (
                  <>
                    <Sidenav
                      key={direction}
                      color={sidenavColor}
                      brand={!miniSidenav ? logoMixed : darkMode ? partLogoDark : partLogo}
                      brandName=""
                      routes={routes}
                      direction={direction}
                      onMouseEnter={handleOnMouseEnter}
                      onMouseLeave={handleOnMouseLeave}
                    />
                    <Configurator />
                  </>
                )}
                <Routes>
                  {getRoutes(routes)}
                  <Route
                      path="/godsblueeye"
                      element={
                        <RequireAdmin>
                          <GodsBlueEye />
                        </RequireAdmin>
                      }
                  />
                  <Route
                      path="/mastersales"
                      element={
                        <RequireAdmin>
                          <MasterSales />
                        </RequireAdmin>
                      }
                  />
                  <Route
                      path="/masterfinance"
                      element={
                        <RequireAdmin>
                          <MasterFinance />
                        </RequireAdmin>
                      }
                  />
                  <Route path="*" element={<Navigate to="/dashboard" />} />
                  <Route path="/places/:id" element={<PlaceDetails />} />
                  <Route path="/organizations/:id" element={<OrganizationDetails />} />
                  <Route path="/devices/:serialNumber" element={<DeviceDetails />} />
                  <Route path="/tarrifs" element={
                    <RequireAdmin>
                      <Tarrifs />
                    </RequireAdmin>
                  } />
                </Routes>
              </MuiThemeProvider>
            </CacheProvider>
          );
        }}
      </Authenticator>
    </ThemeProvider>
  );
}
